// Loose clips on the producer's machine → their private library.
//
// The same front half as a seed (intake, probe, conversion to the pipeline's
// WAV), then the library's own two calls (workers/src/library-clips.ts):
// a manifest that is checked against the quota as a whole — so a producer at
// the limit never ends up with half a folder — and a completion that verifies
// every blob. In between, each new object goes up as its own job through the
// shared put step, so a failed file is retried alone.

import type {
  NewTag,
  CompleteClipsResponse,
  CreateClipsResponse,
  MyClipMeta,
  NewClipManifest,
} from "@shared/api-types.js";
import { sha256Hex } from "@shared/hash/sha256.js";
import type { CompletedFile, SeedbedTransport } from "@shared/upload/publish-seed.js";
import { ApiError } from "@web-shared/lib/api";

import type { IntakeFile } from "./intake";
import { limitProblem } from "./limits";
import type { AudioProbe } from "./probe";
import { UploadProblem } from "./publish";
import { toUploadBytes } from "./to-wav";

export interface ClipToSend extends MyClipMeta {
  file: IntakeFile;
  probe: AudioProbe;
  /** The library folder it lands in; absent = the root. */
  folderId?: string | null;
  /** Tags to store with it: the browser's suggestions, confirmed where the producer tapped ✓. */
  tags?: NewTag[];
}

export interface ClipJob {
  /** The pool key — stable for the upload. */
  id: string;
  hash: string;
  plan: CreateClipsResponse["files"][number];
  bytes: Uint8Array;
  label: string;
}

export interface ClipsSession {
  manifests: NewClipManifest[];
  jobs: ClipJob[];
  /** Names of clips the library already had (same audio). */
  already: string[];
}

/** Convert, hash, and ask the library for plans. Throws UploadProblem with a sentence. */
export async function openClipsUpload(
  transport: SeedbedTransport,
  clips: ClipToSend[],
  onConverting: (done: number, total: number) => void,
): Promise<ClipsSession> {
  const manifests: NewClipManifest[] = [];
  const bytesByHash = new Map<string, { bytes: Uint8Array; label: string }>();
  for (const [i, c] of clips.entries()) {
    onConverting(i, clips.length);
    let bytes: Uint8Array;
    try {
      bytes = await toUploadBytes(c.file.blob, c.file.ext, c.probe);
    } catch {
      throw new UploadProblem(`"${c.name}" couldn't be read as audio.`);
    }
    const problem = limitProblem([{ name: c.name, uploadBytes: bytes.byteLength }]);
    if (problem) throw new UploadProblem(problem);
    const hash = await sha256Hex(bytes);
    if (!bytesByHash.has(hash)) bytesByHash.set(hash, { bytes, label: c.name });
    manifests.push({
      hash,
      sizeBytes: bytes.byteLength,
      name: c.name,
      bpm: c.bpm,
      kind: c.kind,
      durationMs: Math.round(c.probe.durationSec * 1000),
      sampleRate: c.probe.sampleRate,
      channels: c.probe.channels,
      sourceFormat: c.probe.format,
      sourceBitDepth: c.probe.bitDepth,
      folderId: c.folderId ?? null,
      ...(c.tags && c.tags.length > 0 ? { tags: c.tags } : {}),
    });
  }

  let res: CreateClipsResponse;
  try {
    res = await transport.postJson<CreateClipsResponse>("/library/clips", { clips: manifests });
  } catch (err) {
    throw quotaProblem(err) ?? err;
  }
  const jobs: ClipJob[] = res.files
    .filter((f) => !f.exists && f.hash)
    .map((f) => {
      const held = bytesByHash.get(f.hash!)!;
      return { id: f.key, hash: f.hash!, plan: f, bytes: held.bytes, label: held.label };
    });
  return { manifests, jobs, already: res.already };
}

export async function completeClipsUpload(
  transport: SeedbedTransport,
  session: ClipsSession,
  completed: CompletedFile[],
): Promise<CompleteClipsResponse> {
  try {
    return await transport.postJson<CompleteClipsResponse>("/library/clips/complete", {
      clips: session.manifests,
      files: completed,
    });
  } catch (err) {
    throw quotaProblem(err) ?? err;
  }
}

/** The library's 413 as a sentence the producer can act on. */
function quotaProblem(err: unknown): UploadProblem | null {
  // The server's message already says how much was asked and how much is left.
  if (err instanceof ApiError && err.status === 413) {
    return new UploadProblem(err.message || "That won't fit in your library. Nothing was uploaded.");
  }
  return null;
}
